import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Box, Grid, GridItem } from "@chakra-ui/react";
import LegalDocumentList from "./LegalDocumentList";
import LegalDocumentDetail from "./LegalDocumentDetail";
import LegalDocumentForm from "./LegalDocumentForm";
import { useWith2FA } from "../../tools/twoFactor";

/**
 * Fetches all legal documents from the API with optional filters.
 * @param {string} filterType - Type filter value
 * @param {boolean} latestOnly - Whether to fetch only latest versions
 * @param {function} setLoading - Loading state setter
 * @param {function} setDocuments - Documents state setter
 */
async function fetchLegalDocuments(filterType, latestOnly, setLoading, setDocuments) {
    setLoading(true);
    try {
        const queryParts = [];
        if (filterType) queryParts.push(`type=${filterType}`);
        if (latestOnly) queryParts.push("version=latest");
        const params = queryParts.length ? `?${queryParts.join("&")}` : "";
        const response = await axios.get(`/admin/legal/all${params}`);
        setDocuments(response.data || []);
    } catch (error) {
        console.error("Error fetching legal documents:", error);
        toast.error("Erreur lors du chargement des documents");
    } finally {
        setLoading(false);
    }
}

/**
 * Toggles the active status of a document via the 2FA-protected API.
 * @param {object} doc - The document to toggle
 * @param {function} fetchDocuments - Refresh callback
 * @param {function} setSelectedDoc - Selected doc setter
 * @param {function} runWith2FA - The 2FA-aware request runner
 */
async function toggleDocumentActive(doc, fetchDocuments, setSelectedDoc, runWith2FA) {
    const isActive = doc.status === "active";
    const endpoint = isActive ? "deactivate" : "activate";
    try {
        const response = await runWith2FA({ method: "post", url: `/admin/legal/${endpoint}/${doc.id}` });
        toast.success(isActive ? "Document désactivé" : "Document activé");
        fetchDocuments();
        setSelectedDoc(response.data);
    } catch (error) {
        if (error?.message === "2fa_cancelled") return;
        const msg = error.response?.data?.message || error.response?.data;
        toast.error(typeof msg === "string" ? msg : "Erreur lors de l'opération");
    }
}

/**
 * Deletes a document via the 2FA-protected API (blocked server-side if active).
 * @param {object} doc - The document to delete
 * @param {function} fetchDocuments - Refresh callback
 * @param {function} setSelectedDoc - Selected doc setter
 * @param {function} runWith2FA - The 2FA-aware request runner
 */
async function deleteDocument(doc, fetchDocuments, setSelectedDoc, runWith2FA) {
    try {
        await runWith2FA({ method: "delete", url: `/admin/legal/${doc.id}` });
        toast.success("Document supprimé");
        setSelectedDoc(null);
        fetchDocuments();
    } catch (error) {
        if (error?.message === "2fa_cancelled") return;
        const msg = error.response?.data?.message || error.response?.data;
        toast.error(typeof msg === "string" ? msg : "Erreur lors de la suppression");
    }
}

/**
 * Renders the two-column grid with document list and detail views.
 * @param {object} props - All list and detail props
 */
function LegalDocumentsGrid({ documents, selectedDoc, setSelectedDoc, loading, types,
    filterType, setFilterType, filterLanguage, setFilterLanguage, latestOnly, setLatestOnly,
    fetchDocuments, onOpenForm, onToggleActive, onDelete }) {
    return (
        <Grid templateColumns={{ base: "1fr", md: "repeat(12, 1fr)" }} gap="24px">
            <GridItem colSpan={{ base: 12, md: 4 }}>
                <LegalDocumentList documents={documents} selectedDoc={selectedDoc}
                    setSelectedDoc={setSelectedDoc} loading={loading} types={types}
                    filterType={filterType} setFilterType={setFilterType}
                    filterLanguage={filterLanguage} setFilterLanguage={setFilterLanguage}
                    latestOnly={latestOnly} setLatestOnly={setLatestOnly}
                    onRefresh={fetchDocuments} onNewDocument={() => onOpenForm(null)} />
            </GridItem>
            <GridItem colSpan={{ base: 12, md: 8 }}>
                <LegalDocumentDetail document={selectedDoc} onEdit={() => onOpenForm(selectedDoc)}
                    onToggleActive={onToggleActive} onDelete={onDelete} />
            </GridItem>
        </Grid>
    );
}

/**
 * Parent component for legal document management.
 * Manages document list state, selected document, form dialog, and API interactions.
 * @returns {React.ReactElement}
 */
function LegalDocuments() {
    const [documents, setDocuments] = useState([]);
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editingDoc, setEditingDoc] = useState(null);
    const [filterType, setFilterType] = useState("");
    const [filterLanguage, setFilterLanguage] = useState("fr");
    const [latestOnly, setLatestOnly] = useState(true);
    const runWith2FA = useWith2FA();

    const visibleDocuments = useMemo(
        () => (filterLanguage ? documents.filter((doc) => doc.language === filterLanguage) : documents),
        [documents, filterLanguage]
    );

    const fetchDocuments = useCallback(() => {
        return fetchLegalDocuments(filterType, latestOnly, setLoading, setDocuments);
    }, [filterType, latestOnly]);

    const fetchTypes = useCallback(async () => {
        try { setTypes((await axios.get("/admin/legal/types")).data || []); }
        catch (error) { console.error("Error fetching document types:", error); }
    }, []);

    useEffect(() => { fetchDocuments(); }, [fetchDocuments]);
    useEffect(() => { fetchTypes(); }, [fetchTypes]);
    useEffect(() => {
        setSelectedDoc((prev) =>
            prev && visibleDocuments.some((doc) => doc.id === prev.id) ? prev : (visibleDocuments[0] || null));
    }, [visibleDocuments]);

    /** @param {object|null} doc */
    const handleOpenForm = (doc) => { setEditingDoc(doc || null); setFormOpen(true); };
    const handleCloseForm = () => { setFormOpen(false); setEditingDoc(null); };
    const handleSuccess = (saved) => { fetchDocuments(); setSelectedDoc(saved); handleCloseForm(); };

    return (
        <Box p="4px" pb="40px" maxH="100vh" overflow="auto">
            <LegalDocumentsGrid documents={visibleDocuments} selectedDoc={selectedDoc}
                setSelectedDoc={setSelectedDoc} loading={loading} types={types}
                filterType={filterType} setFilterType={setFilterType}
                filterLanguage={filterLanguage} setFilterLanguage={setFilterLanguage}
                latestOnly={latestOnly} setLatestOnly={setLatestOnly}
                fetchDocuments={fetchDocuments} onOpenForm={handleOpenForm}
                onToggleActive={(doc) => toggleDocumentActive(doc, fetchDocuments, setSelectedDoc, runWith2FA)}
                onDelete={(doc) => deleteDocument(doc, fetchDocuments, setSelectedDoc, runWith2FA)} />
            {formOpen && (
                <LegalDocumentForm open={formOpen} document={editingDoc} types={types}
                    onClose={handleCloseForm} onSuccess={handleSuccess} />
            )}
        </Box>
    );
}

export default LegalDocuments;
